import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { StatusBadge } from "@/lib/constants";
import { Progress } from "@/components/ui/progress";
import { Award, Clock, Trophy, GraduationCap, User, Phone, Mail, Building2, ArrowRight, Star, Target, CheckCircle2, BookOpen } from "lucide-react";

const BADGE_ICON = { award: Award, trophy: Trophy, target: Target, clock: Clock, star: Star, graduation: GraduationCap, check: CheckCircle2 };

export default function StudentProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/students/${id}`).then((r) => setD(r.data)).catch(() => setD(false)); }, [id]);
  if (d === null) return <PageLoader />;
  if (d === false) return <EmptyState icon={User} title="تعذّر تحميل ملف الطالب" />;

  const s = d.student, g = d.gamification;
  const earned = g.badges.filter((b) => b.earned);

  return (
    <div className="animate-fade-in space-y-6">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-navy"><ArrowRight className="h-4 w-4" /> رجوع</button>

      <div className="rounded-2xl border border-slate-100 bg-card p-6 soft-shadow">
        <div className="flex flex-wrap items-center gap-5">
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-navy text-2xl font-bold text-gold">{s.name?.charAt(0)}</div>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-extrabold text-navy">{s.name}</h1>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-400">
              <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {s.email}</span>
              <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {s.phone || "—"}</span>
              <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {s.unit_name || "بدون وحدة"}</span>
              <span>{s.university_status || "—"}</span>
            </div>
          </div>
          <div className="rounded-xl bg-amber-50 px-5 py-3 text-center">
            <p className="font-heading text-3xl font-extrabold text-gold-dark">{g.points}</p>
            <p className="text-xs text-slate-500">نقطة • {g.level_title}</p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Trophy} label="الترتيب في الوحدة" value={`${d.rank.position} / ${d.rank.total}`} />
        <Stat icon={Clock} label="الساعات العلمية" value={`${g.attended_hours}/${g.total_hours}`} />
        <Stat icon={GraduationCap} label="محاضرات حضرها" value={g.attended_lectures} />
        <Stat icon={Award} label="برامج مكتملة" value={g.completed_programs} />
      </div>

      <div className="rounded-2xl border border-slate-100 bg-card p-6 soft-shadow">
        <h3 className="mb-4 font-heading text-lg font-bold text-navy">شارات الإنجاز</h3>
        <div className="flex flex-wrap gap-3">
          {g.badges.map((b) => { const I = BADGE_ICON[b.icon] || Award; return (
            <div key={b.key} className={`flex items-center gap-2 rounded-xl border px-4 py-2 ${b.earned ? "border-gold/40 bg-gold/10 text-gold-dark" : "border-slate-100 bg-slate-50 text-slate-300"}`}>
              <I className="h-5 w-5" /> <span className="text-sm font-semibold">{b.label}</span>
            </div>
          ); })}
        </div>
        {earned.length === 0 && <p className="mt-2 text-xs text-slate-400">لم يحصل على شارات بعد</p>}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-100 bg-card p-6 soft-shadow">
          <h3 className="mb-4 font-heading text-lg font-bold text-navy">البرامج</h3>
          {d.programs.length === 0 ? <p className="text-sm text-slate-400">لا برامج</p> : d.programs.map((p) => (
            <div key={p.id} className="mb-3 rounded-xl border border-slate-100 p-4">
              <div className="flex items-center justify-between"><span className="font-semibold text-navy">{p.name}</span><StatusBadge status={p.status} /></div>
              <div className="mt-2 flex items-center justify-between text-xs text-slate-500"><span>الحضور {p.attendance_percent}% • {p.attended_hours}/{p.total_hours} ساعة</span><span className={p.passed ? "text-greenx font-semibold" : "text-slate-400"}>{p.passed ? "ناجح" : `النتيجة ${p.final}%`}</span></div>
              <Progress value={p.attendance_percent} className="mt-2 h-1.5" />
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-slate-100 bg-card p-6 soft-shadow">
          <h3 className="mb-4 font-heading text-lg font-bold text-navy">المحاضرات التي حضرها ({d.attended_lectures.length})</h3>
          {d.attended_lectures.length === 0 ? <p className="text-sm text-slate-400">لا سجل حضور</p> : (
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {d.attended_lectures.map((l, i) => (
                <div key={i} className="rounded-xl bg-slate-50 p-3">
                  <p className="text-sm font-semibold text-navy">{l.title}</p>
                  <p className="text-xs text-slate-400">{l.program_name} • {l.date} • {l.start_time} • {l.hours} ساعة</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-card p-5 soft-shadow">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy/5 text-navy"><Icon className="h-5 w-5" /></div>
      <p className="mt-3 font-heading text-2xl font-extrabold text-navy">{value}</p>
      <p className="text-xs text-slate-400">{label}</p>
    </div>
  );
}
